function NYTDemo() {
  return (
    <div className="min-h-screen">
      {/* Article Header */}
      <article className="max-w-[1200px] mx-auto px-8 py-12">
        
        {/* Kicker / Section label */}
        <div className="border-t-4 border-black dark:border-gray-100 pt-3 mb-6 w-fit">
          <p className="font-sans text-xs font-bold tracking-wider uppercase text-gray-900 dark:text-gray-100">
            Intelligence Report
          </p>
        </div>

        {/* Main Headline - Large Serif */}
        <h1 className="font-serif text-6xl md:text-7xl font-bold leading-tight mb-6 text-gray-900 dark:text-gray-100 max-w-4xl">
          Critical Infrastructure Under Siege as Cyber Threats Escalate
        </h1>

        {/* Deck / Subheadline */}
        <p className="font-sans text-xl md:text-2xl leading-relaxed text-gray-700 dark:text-gray-300 mb-8 max-w-3xl break-words">
          Intelligence agencies warn of coordinated attacks targeting power grids and water systems 
          across multiple regions as nation-state actors increase cyber operations.
        </p>

        {/* Byline & Metadata */}
        <div className="border-t border-b border-gray-300 dark:border-gray-700 py-4 mb-12">
          <div className="flex items-center gap-6 text-sm">
            <div className="font-sans">
              <span className="text-gray-500 dark:text-gray-400">By </span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">Sarah Chen</span>
              <span className="text-gray-500 dark:text-gray-400"> and </span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">Michael Torres</span>
            </div>
            <div className="text-gray-500 dark:text-gray-400 font-sans">
              April 3, 2026
            </div>
          </div>
        </div>

        {/* Lead Art Placeholder */}
        <figure className="mb-12">
          <div className="bg-gray-200 dark:bg-gray-800 aspect-[16/9] flex items-center justify-center">
            <span className="font-sans text-sm text-gray-500 dark:text-gray-400">[Lead Image]</span>
          </div>
          <figcaption className="mt-3 text-sm text-gray-600 dark:text-gray-400 font-sans">
            Security operations centers have detected an unprecedented surge in coordinated attacks. 
            <span className="text-gray-500"> Credit: Department of Homeland Security</span>
          </figcaption>
        </figure>

        {/* Article Body - Two Column Layout on Desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Main Content */}
          <div className="lg:col-span-8">
            
            {/* Dropcap Paragraph */}
            <p className="font-serif text-lg leading-relaxed mb-6 text-gray-900 dark:text-gray-100">
              <span className="float-left font-serif text-7xl leading-none pr-2 pt-1 font-bold">F</span>
              ederal intelligence officials issued urgent warnings on Thursday about a coordinated campaign 
              targeting critical infrastructure across the United States and allied nations, marking what 
              cybersecurity experts describe as an unprecedented escalation in state-sponsored cyber warfare.
            </p>

            <p className="font-serif text-lg leading-relaxed mb-6 text-gray-900 dark:text-gray-100">
              The attacks, which began in late February, have targeted power generation facilities, 
              water treatment plants, and telecommunications networks in at least seven countries. 
              Officials speaking on condition of anonymity said the sophistication and coordination 
              of the operations point to well-resourced nation-state actors.
            </p>

            <p className="font-serif text-lg leading-relaxed mb-6 text-gray-900 dark:text-gray-100">
              "We are witnessing a fundamental shift in how adversaries approach critical infrastructure," 
              said Jennifer Park, director of the Cybersecurity and Infrastructure Security Agency. 
              "The level of coordination and technical capability displayed in these operations is 
              deeply concerning."
            </p>

            {/* Pull Quote */}
            <aside className="border-l-4 border-gray-900 dark:border-gray-100 pl-6 my-12">
              <p className="font-serif text-2xl md:text-3xl leading-snug italic text-gray-900 dark:text-gray-100 mb-3">
                "We are witnessing a fundamental shift in how adversaries approach critical infrastructure."
              </p>
              <p className="font-sans text-sm font-semibold text-gray-700 dark:text-gray-300">
                Jennifer Park, CISA Director
              </p>
            </aside>

            {/* Section Heading */}
            <h2 className="font-serif text-3xl md:text-4xl font-bold leading-tight mb-6 mt-12 text-gray-900 dark:text-gray-100">
              A Pattern Emerges
            </h2>

            <p className="font-serif text-lg leading-relaxed mb-6 text-gray-900 dark:text-gray-100">
              Analysis of network traffic and attack signatures reveals a disturbing pattern. 
              The intrusions followed similar playbooks: initial reconnaissance through supply chain 
              vulnerabilities, establishment of persistent access, and careful mapping of operational 
              technology systems before any disruptive actions.
            </p>

            <p className="font-serif text-lg leading-relaxed mb-6 text-gray-900 dark:text-gray-100">
              Intelligence agencies have attributed the campaign to actors associated with foreign 
              governments, though officials declined to name specific nations pending ongoing 
              diplomatic discussions. The attribution is based on technical indicators, including 
              malware samples, command and control infrastructure, and operational patterns consistent 
              with known threat groups.
            </p>

            {/* Subsection */}
            <h3 className="font-serif text-2xl font-bold leading-tight mb-4 mt-10 text-gray-900 dark:text-gray-100">
              Industry Response
            </h3>

            <p className="font-serif text-lg leading-relaxed mb-6 text-gray-900 dark:text-gray-100">
              Major utility companies have implemented emergency security measures, including 
              enhanced monitoring and segmentation of operational technology networks. The Edison 
              Electric Institute, representing investor-owned electric companies, issued guidance 
              to its members on Thursday afternoon.
            </p>

            <ul className="space-y-3 mb-6 ml-6">
              <li className="font-serif text-lg leading-relaxed text-gray-900 dark:text-gray-100">
                <span className="font-sans text-gray-600 dark:text-gray-400">■</span> Enhanced network monitoring deployed across 47 utilities
              </li>
              <li className="font-serif text-lg leading-relaxed text-gray-900 dark:text-gray-100">
                <span className="font-sans text-gray-600 dark:text-gray-400">■</span> Emergency response teams placed on high alert
              </li>
              <li className="font-serif text-lg leading-relaxed text-gray-900 dark:text-gray-100">
                <span className="font-sans text-gray-600 dark:text-gray-400">■</span> Coordination with federal agencies increased significantly
              </li>
            </ul>

            <p className="font-serif text-lg leading-relaxed mb-6 text-gray-900 dark:text-gray-100">
              Robert Chen, Chief Security Officer at NorthStar Energy, said his company detected 
              and blocked multiple intrusion attempts over the past three weeks. "The sophistication 
              is unlike anything we've seen before," he said in an interview. "These actors have 
              clearly studied our systems extensively."
            </p>

          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            
            {/* Related Coverage Box */}
            <div className="border-t-2 border-gray-900 dark:border-gray-100 pt-4 mb-8">
              <h3 className="font-sans text-xs font-bold tracking-wider uppercase mb-4 text-gray-700 dark:text-gray-300">
                Related Coverage
              </h3>
              
              <div className="space-y-6">
                <article>
                  <h4 className="font-serif text-lg font-bold leading-snug mb-2 text-gray-900 dark:text-gray-100">
                    Power Grid Vulnerabilities Exposed in Recent Drill
                  </h4>
                  <p className="font-sans text-sm text-gray-600 dark:text-gray-400">
                    March 28, 2026
                  </p>
                </article>

                <article>
                  <h4 className="font-serif text-lg font-bold leading-snug mb-2 text-gray-900 dark:text-gray-100">
                    China and Russia Expand Cyber Capabilities
                  </h4>
                  <p className="font-sans text-sm text-gray-600 dark:text-gray-400">
                    March 15, 2026
                  </p>
                </article>

                <article>
                  <h4 className="font-serif text-lg font-bold leading-snug mb-2 text-gray-900 dark:text-gray-100">
                    Water Systems Face Growing Digital Threats
                  </h4>
                  <p className="font-sans text-sm text-gray-600 dark:text-gray-400">
                    March 2, 2026
                  </p>
                </article>
              </div>
            </div>

            {/* Key Points Box */}
            <div className="bg-gray-100 dark:bg-gray-800 p-6 mb-8">
              <h3 className="font-sans text-xs font-bold tracking-wider uppercase mb-4 text-gray-700 dark:text-gray-300">
                Key Points
              </h3>
              <ul className="space-y-3">
                <li className="font-sans text-sm leading-relaxed text-gray-900 dark:text-gray-100">
                  Coordinated attacks detected across seven countries
                </li>
                <li className="font-sans text-sm leading-relaxed text-gray-900 dark:text-gray-100">
                  Nation-state actors attributed to campaign
                </li>
                <li className="font-sans text-sm leading-relaxed text-gray-900 dark:text-gray-100">
                  Power grids and water systems primary targets
                </li>
                <li className="font-sans text-sm leading-relaxed text-gray-900 dark:text-gray-100">
                  Utilities implement emergency protocols
                </li>
              </ul>
            </div>

            {/* Subscribe CTA */}
            <div className="border border-gray-300 dark:border-gray-700 p-6">
              <p className="font-sans text-sm font-semibold mb-3 text-gray-900 dark:text-gray-100">
                Intelligence Briefing
              </p>
              <p className="font-sans text-sm leading-relaxed mb-4 text-gray-700 dark:text-gray-300">
                Get daily intelligence updates delivered to your inbox.
              </p>
              <button className="w-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 font-sans text-sm font-semibold py-2 px-4 hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors">
                Subscribe
              </button>
            </div>

          </aside>

        </div>

      </article>
    </div>
  )
}

export default NYTDemo
